/**
 * Balance Sheet — statement of financial position in the UK FRS 102 layout (fixed assets,
 * current assets, creditors, net assets = capital and reserves), with the prior financial
 * year end as the comparative column.
 *
 * Reads from POST /api/financial-config/balance-sheet/generate.
 * Includes a Cash Reconciliation panel comparing GL bank balance vs real bank
 * API balances (Revolut/PayPal/Wise/Shopify) — surfaces the unjournaled gap.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Alert, CircularProgress, Stack, Card, CardContent,
  Grid, Button, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Divider, Tooltip,
} from '@mui/material';
import {
  Refresh, AccountBalance,
  ManageSearch as DrillIcon,
} from '@mui/icons-material';
import { generateBalanceSheet } from '../../services/financialConfigService';
import StatementTable from './StatementTable';
import AccountEntriesDialog from './AccountEntriesDialog';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (n) => {
  const v = Number(n || 0);
  return (v < 0 ? '-' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


function CashReconPanel({ recon, onDrillAccount }) {
  if (!recon || recon.error) return null;
  const gap = Number(recon.gap_usd || 0);
  const gapColor = Math.abs(gap) < 1 ? 'success.main' : Math.abs(gap) > 100000 ? 'error.main' : 'warning.main';
  return (
    <Card sx={{ mb: 2, border: '2px solid', borderColor: gapColor }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AccountBalance sx={{ color: gapColor }} />
          <Typography variant="h6">Cash Reconciliation — GL vs Real Bank API</Typography>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">GL Bank + Pending</Typography>
            <Typography variant="h6">{fmtSigned(recon.gl_cash_total_usd)}</Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              Bank: {fmtSigned(recon.gl_bank_account_usd)} · Pending: {fmtSigned(recon.gl_payments_pending_usd)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Real (Revolut/PayPal/Wise/Shopify API)</Typography>
            <Typography variant="h6">{fmtSigned(recon.real_cash_total_usd)}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Reconciliation Gap</Typography>
            <Typography variant="h6" sx={{ color: gapColor, fontWeight: 700 }}>
              {fmtSigned(gap)}
            </Typography>
          </Grid>
        </Grid>
        <Alert severity={Math.abs(gap) < 1 ? 'success' : 'warning'} sx={{ mt: 2 }}>
          {recon.gap_explanation}
        </Alert>
        {recon.per_account && recon.per_account.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Per-GL-Account Reconciliation (sorted by gap size)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>GL Account</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>GL Balance</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Real Balance</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Gap (Real − GL)</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}># Real Accts</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Fix</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recon.per_account.map((a) => {
                    const gapColor = Math.abs(a.gap_usd) < 1 ? 'success.main' :
                                     Math.abs(a.gap_usd) > 50000 ? 'error.main' : 'warning.main';
                    return (
                      <TableRow key={a.gl_account_id} hover>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          <strong>{a.account_code}</strong> — {a.account_name}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{fmtSigned(a.gl_balance_usd)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{fmtSigned(a.real_balance_usd)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.9rem', color: gapColor, fontWeight: 600 }}>
                          {fmtSigned(a.gap_usd)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {a.real_account_count}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={`View pending bank-queue rows for ${a.account_code} — fix/reclassify directly`}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onDrillAccount && onDrillAccount(a.account_code)}
                            >
                              <DrillIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Real Balance = sum of mapped real-bank balances (Revolut/Wise) joined via connector_gl_mappings.
              Accounts with 0 real accts mapped need a row added to connector_gl_mappings.
            </Typography>
          </Box>
        )}

        {recon.real_balances_by_account && recon.real_balances_by_account.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Top real accounts (raw API):</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem' }}>Source</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>Account / Currency</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Local</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem' }}>USD</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>Snapshot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recon.real_balances_by_account.slice(0, 10).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <Chip size="small" label={r.source} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {r.account_label || r.account_key} {r.currency && `(${r.currency})`}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{fmt(r.balance_local)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{fmtSigned(r.balance_usd)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{r.snapshot_date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}


// Prior financial year end = the day before the current financial year starts.
const dayBefore = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

// Statement rows from the classified view of each column (current, comparative), merged by line
// key and account code so a line or account present in only one year still shows.
function buildRows(cols) {
  const sec = (key) => cols.map(c => c?.sections.find(s => s.key === key));
  const lineRows = (key) => {
    const secs = sec(key);
    const keys = [];
    secs.forEach(s => (s?.lines || []).forEach(l => { if (!keys.includes(l.key)) keys.push(l.key); }));
    return keys.map(k => {
      const ls = secs.map(s => s?.lines.find(l => l.key === k));
      const codes = [];
      ls.forEach(l => (l?.accounts || []).forEach(a => { if (!codes.includes(a.account_code)) codes.push(a.account_code); }));
      return {
        kind: 'line',
        label: ls.find(Boolean).label,
        values: ls.map(l => (l ? l.total_usd : 0)),
        accounts: codes.map(code => {
          const as = ls.map(l => l?.accounts.find(a => a.account_code === code));
          return { code, name: as.find(Boolean).account_name, values: as.map(a => (a ? a.balance_usd : 0)) };
        }),
      };
    });
  };
  const sub = (field) => cols.map(c => (c ? c.subtotals[field] : null));
  const label = (key) => sec(key).find(Boolean)?.label;
  return [
    { kind: 'section', label: label('non_current_assets') },
    ...lineRows('non_current_assets'),
    { kind: 'subtotal', label: 'Total fixed assets', values: sub('fixed_assets_usd') },
    { kind: 'section', label: label('current_assets') },
    ...lineRows('current_assets'),
    { kind: 'subtotal', label: 'Total current assets', values: sub('current_assets_usd') },
    { kind: 'section', label: label('current_liabilities') },
    ...lineRows('current_liabilities'),
    { kind: 'subtotal', label: 'Total creditors due within one year', values: sub('current_liabilities_usd') },
    { kind: 'subtotal', label: 'Net current assets / (liabilities)', values: sub('net_current_assets_usd') },
    { kind: 'subtotal', label: 'Total assets less current liabilities', values: sub('total_assets_less_current_liabilities_usd') },
    { kind: 'section', label: label('non_current_liabilities') },
    ...lineRows('non_current_liabilities'),
    { kind: 'subtotal', label: 'Total creditors due after more than one year', values: sub('long_term_liabilities_usd') },
    { kind: 'total', label: 'Net assets', values: sub('net_assets_usd') },
    { kind: 'section', label: label('equity') },
    ...lineRows('equity'),
    { kind: 'total', label: 'Total capital and reserves', values: sub('total_equity_usd') },
  ];
}

export default function BalanceSheet() {
  const today = new Date().toISOString().slice(0, 10);
  const [asOfDate, setAsOfDate] = useState(today);
  const [data, setData] = useState(null);
  const [prior, setPrior] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null);

  // Journal entries behind an account's balance, up to the as-of date, in an overlay.
  const openEntries = (accountCode, accountName) => setDrill({ accountCode, accountName, endDate: data?.as_of_date || asOfDate });

  const load = async () => {
    setLoading(true);
    setError(null);
    setPrior(null);
    try {
      const r = await generateBalanceSheet({ as_of_date: asOfDate });
      if (!r.success) {
        setError(r.error || 'Unknown error');
      } else {
        setData(r.data);
        // Comparative column loads after the current year is on screen; if it fails the
        // statement simply shows one column.
        const fyStart = r.data.classified?.fiscal_year_start;
        if (fyStart) {
          generateBalanceSheet({ as_of_date: dayBefore(fyStart) })
            .then(p => { if (p.success) setPrior(p.data); })
            .catch(() => {});
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cls = data?.classified;
  const columns = cls ? [`As at ${data.as_of_date}`, ...(prior?.classified ? [`As at ${prior.as_of_date}`] : [])] : [];
  const rows = cls ? buildRows([cls, ...(prior?.classified ? [prior.classified] : [])]) : [];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Balance Sheet</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="date"
            label="As of"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Generate
          </Button>
        </Stack>
      </Stack>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {data && !loading && (
        <>
          {data.balance_check && !data.balance_check.is_balanced && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Balance sheet imbalance: delta {fmtSigned(data.balance_check.delta_usd)}.
              {' '}{data.balance_check.note}
              {data.balance_check.unbalanced_entries_error && (
                <Box sx={{ mt: 1 }}>{data.balance_check.unbalanced_entries_error}.</Box>
              )}
              {(data.balance_check.unbalanced_entries || []).length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {data.balance_check.unbalanced_entries.map((e) => (
                    <li key={e.je_id}>
                      {e.entry_number} ({e.entry_date}): debits − credits = {e.native_diff} {e.base_currency} ({fmtSigned(e.usd_diff)})
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {cls ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6">Statement of financial position</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Click a line to see its accounts; the magnifier opens that account's journal entries.
                </Typography>
                <StatementTable columns={columns} rows={rows} onDrill={openEntries} />
                {data.balance_check?.is_balanced && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                    Balances: net assets equal capital and reserves.
                  </Typography>
                )}
              </CardContent>
            </Card>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>The statement could not be laid out for this date.</Alert>
          )}

          {data.cash_reconciliation && <CashReconPanel recon={data.cash_reconciliation} onDrillAccount={(code) => openEntries(code)} />}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            As of {data.as_of_date} · Currency: {data.currency} (each entry converted at its month's rate) · Brackets are negative amounts.
          </Typography>
        </>
      )}
      <AccountEntriesDialog drill={drill} onClose={() => setDrill(null)} />
    </Box>
  );
}
